import type { ReportSchedule, ReportScheduleFrequency } from '@api/reportSchedules';

/**
 * Scheduling-time helpers shared by ScheduleReportModal (edit / alert flows) and
 * EmailComposerDrawer (the capture-mode composer). Extracted rather than copied — two
 * next-run calculators that drift apart would show two different "first email arrives"
 * promises for the same schedule.
 */

export const WEEKDAYS = [
    { value: 1, label: 'Mon' },
    { value: 2, label: 'Tue' },
    { value: 3, label: 'Wed' },
    { value: 4, label: 'Thu' },
    { value: 5, label: 'Fri' },
    { value: 6, label: 'Sat' },
    { value: 0, label: 'Sun' },
];

export const ALL_DAYS = [0, 1, 2, 3, 4, 5, 6];

/** Daily starts from every day; weekly starts from Monday unless days were already narrowed. */
export function daysForFrequency(frequency: ReportScheduleFrequency, days: number[]): number[] {
    if (frequency === 'DAILY') return ALL_DAYS;
    return days.length === ALL_DAYS.length ? [1] : days;
}

function ordinal(n: number): string {
    const s = ['th', 'st', 'nd', 'rd'];
    const v = n % 100;
    return n + (s[(v - 20) % 10] || s[v] || s[0]);
}

export function formatCadence(
    schedule: Pick<ReportSchedule, 'frequency' | 'triggerTime' | 'triggerDaysOfWeek' | 'triggerDayOfMonth'>,
): string {
    const at = `at ${schedule.triggerTime} IST`;
    if (schedule.frequency === 'MONTHLY') {
        return `Monthly on the ${ordinal(schedule.triggerDayOfMonth ?? 1)} ${at}`;
    }
    const days = schedule.triggerDaysOfWeek;
    if (schedule.frequency === 'WEEKLY') {
        const sends = WEEKDAYS.filter((d) => days?.includes(d.value)).map((d) => d.label);
        return `Every ${sends.join(', ')} ${at}`;
    }
    if (!days?.length) return `Daily ${at}`;
    const skipped = WEEKDAYS.filter((d) => !days.includes(d.value)).map((d) => d.label);
    return `Daily except ${skipped.join(', ')} ${at}`;
}

export function computeNextRunIst(
    frequency: ReportScheduleFrequency,
    triggerTime: string,
    days?: number[] | null,
    dayOfMonth?: number,
): Date | null {
    const match = /^([01]\d|2[0-3]):([0-5]\d)$/.exec(triggerTime);
    if (!match) return null;
    const h = Number(match[1]);
    const m = Number(match[2]);

    const istNow = new Date(
        new Date().toLocaleString('en-US', { timeZone: 'Asia/Kolkata' }),
    );
    const target = new Date(istNow);
    target.setHours(h, m, 0, 0);

    if (frequency !== 'MONTHLY') {
        if (days?.length === 0) return null;
        const sends = days ?? ALL_DAYS;
        if (target <= istNow) target.setDate(target.getDate() + 1);
        while (!sends.includes(target.getDay())) target.setDate(target.getDate() + 1);
    } else {
        const dom = dayOfMonth ?? 1;
        target.setDate(dom);
        target.setHours(h, m, 0, 0);
        if (target <= istNow) {
            target.setMonth(target.getMonth() + 1);
            target.setDate(dom);
            target.setHours(h, m, 0, 0);
        }
    }

    const yyyy = target.getFullYear();
    const mm = String(target.getMonth() + 1).padStart(2, '0');
    const dd = String(target.getDate()).padStart(2, '0');
    const hh = String(target.getHours()).padStart(2, '0');
    const mi = String(target.getMinutes()).padStart(2, '0');
    return new Date(`${yyyy}-${mm}-${dd}T${hh}:${mi}:00+05:30`);
}

export function formatNextRun(date: Date): string {
    return (
        date.toLocaleString('en-US', {
            timeZone: 'Asia/Kolkata',
            weekday: 'short',
            month: 'short',
            day: 'numeric',
            hour: 'numeric',
            minute: '2-digit',
            hour12: true,
        }) + ' IST'
    );
}
